import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { cityAliasesService } from "@src/pages/VenueAliases/cityAliasesService";
import { CityAlias } from "@src/pages/VenueAliases/types";

interface CityAliasesState {
	aliases: Array<CityAlias>;
	total: number;
	currentPage: number;
	itemsPerPage: number;
	listLoading: "idle" | "pending" | "succeeded" | "failed";
	mutationLoading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
}

const initialState: CityAliasesState = {
	aliases: [],
	total: 0,
	currentPage: 1,
	itemsPerPage: 30,
	listLoading: "idle",
	mutationLoading: "idle",
	error: null,
};

export const fetchCityAliases = createAsyncThunk(
	"cityAliases/fetchCityAliases",
	async (
		{
			page,
			pageSize,
			searchTerm,
			sortBy,
			sortDirection,
		}: {
			page: number;
			pageSize: number;
			searchTerm?: string;
			sortBy?: "sfCity" | "mpCity" | "state" | "createdBy" | "createdOn";
			sortDirection?: "asc" | "desc";
		},
		{ rejectWithValue },
	) => {
		try {
			return await cityAliasesService.listCityAliases({ page, pageSize, searchTerm, sortBy, sortDirection });
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to load city aliases.");
		}
	},
);

export const deleteCityAlias = createAsyncThunk(
	"cityAliases/deleteCityAlias",
	async ({ cityAliasId }: { cityAliasId: number | string }, { rejectWithValue }) => {
		try {
			await cityAliasesService.deleteCityAlias(cityAliasId);
			toast.success("Alias deleted");
			return cityAliasId;
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to delete city alias.");
		}
	},
);

export const cityAliasesSlice = createSlice({
	name: "cityAliases",
	initialState,
	reducers: {
		setCurrentPage(state, action: PayloadAction<number>) {
			state.currentPage = action.payload;
		},
		setItemsPerPage(state, action: PayloadAction<number>) {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchCityAliases.pending, (state) => {
				state.listLoading = "pending";
				state.error = null;
			})
			.addCase(fetchCityAliases.fulfilled, (state, action) => {
				state.listLoading = "succeeded";
				state.aliases = action.payload.items;
				state.total = action.payload.total;
			})
			.addCase(fetchCityAliases.rejected, (state, action) => {
				state.listLoading = "failed";
				state.error = action.payload as string;
			})
			.addCase(deleteCityAlias.pending, (state) => {
				state.mutationLoading = "pending";
				state.error = null;
			})
			.addCase(deleteCityAlias.fulfilled, (state, action) => {
				state.mutationLoading = "succeeded";
				state.aliases = state.aliases.filter((alias) => String(alias.id) !== String(action.payload));
				state.total = Math.max(0, state.total - 1);
			})
			.addCase(deleteCityAlias.rejected, (state, action) => {
				state.mutationLoading = "failed";
				state.error = action.payload as string;
				toast.error(action.payload as string);
			});
	},
});

export const { setCurrentPage, setItemsPerPage } = cityAliasesSlice.actions;

export default cityAliasesSlice.reducer;
